import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Volume2, VolumeX, Vibrate, Moon, Zap, Play, Bell, ChevronRight, Check } from 'lucide-react';
import { loadSettings, saveSettings, playNewOrderSound, playUrgentOrderSound, playPaymentSound, playOverdueSound } from './SoundSystem';

const soundTypes = [
  { id: 'new-order', label: 'New Order', description: 'Soft luxury chime', play: playNewOrderSound },
  { id: 'urgent', label: 'Urgent Order', description: 'Premium priority alert', play: playUrgentOrderSound },
  { id: 'payment', label: 'Payment Received', description: 'Success tone', play: playPaymentSound },
  { id: 'overdue', label: 'Overdue Reminder', description: 'Soft pulse alert', play: playOverdueSound },
];

const TailorSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(loadSettings);
  const [playingId, setPlayingId] = useState(null);

  const update = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    saveSettings(next);
  };

  const testSound = (type) => {
    setPlayingId(type.id);
    type.play(settings.volume);
    setTimeout(() => setPlayingId(null), 1200);
  };

  const ToggleRow = ({ testId, label, description, value, onChange, icon: Icon, iconColor }) => (
    <div className="flex items-center justify-between py-3.5 border-b" style={{ borderColor: '#F0F0F0' }}>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: iconColor + '12' }}>
          <Icon className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-[13px]" style={{ color: '#0F0F0F' }}>{label}</p>
          {description && <p className="text-[11px]" style={{ color: '#595959' }}>{description}</p>}
        </div>
      </div>
      <button data-testid={testId} onClick={() => onChange(!value)} className="relative w-11 h-6 rounded-full transition-colors"
        style={{ backgroundColor: value ? '#166534' : '#E5E5E5' }}>
        <div className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
          style={{ left: value ? 22 : 2 }} />
      </button>
    </div>
  );

  return (
    <div data-testid="tailor-settings" className="min-h-screen"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>

      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center gap-2 px-3 py-3 border-b bg-white" style={{ borderColor: '#E5E5E5' }}>
        <button data-testid="back-btn" onClick={() => navigate('/tailor-dashboard')} className="p-1">
          <ChevronLeft className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-base" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Sound Settings</h1>
      </div>

      <div className="px-4 py-4 space-y-5">
        {/* Master Sound */}
        <div className="bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
          <p className="text-[10px] tracking-[0.12em] uppercase mb-3" style={{ color: '#595959' }}>Master Controls</p>

          <ToggleRow testId="toggle-sound" label="Sound" description={settings.soundEnabled ? 'Notifications will play audio' : 'All sounds are off'}
            value={settings.soundEnabled} onChange={v => update('soundEnabled', v)}
            icon={settings.soundEnabled ? Volume2 : VolumeX} iconColor="#D4AF37" />

          {/* Volume Slider */}
          <div className="py-3.5 border-b" style={{ borderColor: '#F0F0F0' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[13px]" style={{ color: settings.soundEnabled ? '#0F0F0F' : '#BFBFBF' }}>Volume</p>
              <span className="text-[12px] font-medium tabular-nums" style={{ color: settings.soundEnabled ? '#0F0F0F' : '#BFBFBF' }}>
                {Math.round(settings.volume * 100)}%
              </span>
            </div>
            <input data-testid="volume-slider" type="range" min="0" max="100" value={Math.round(settings.volume * 100)}
              onChange={e => update('volume', parseInt(e.target.value) / 100)}
              disabled={!settings.soundEnabled}
              className="w-full h-1 rounded appearance-none cursor-pointer disabled:opacity-30"
              style={{ accentColor: '#D4AF37', backgroundColor: '#E5E5E5' }} />
            <div className="flex justify-between mt-1">
              <span className="text-[9px]" style={{ color: '#BFBFBF' }}>Quiet</span>
              <span className="text-[9px]" style={{ color: '#BFBFBF' }}>Loud</span>
            </div>
          </div>

          <ToggleRow testId="toggle-vibration" label="Vibration" description="Haptic feedback with alerts"
            value={settings.vibrationEnabled} onChange={v => update('vibrationEnabled', v)}
            icon={Vibrate} iconColor="#E5B2B9" />

          <ToggleRow testId="toggle-night-mode" label="Night Mode" description={settings.nightMode ? 'All sounds suppressed' : 'Silence during night hours'}
            value={settings.nightMode} onChange={v => update('nightMode', v)}
            icon={Moon} iconColor="#595959" />

          <ToggleRow testId="toggle-urgent-only" label="Urgent Only" description={settings.urgentOnly ? 'Only urgent orders trigger sound' : 'All order types play sound'}
            value={settings.urgentOnly} onChange={v => update('urgentOnly', v)}
            icon={Zap} iconColor="#991B1B" />
        </div>

        {/* Sound Preview */}
        <div className="bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
          <p className="text-[10px] tracking-[0.12em] uppercase mb-3" style={{ color: '#595959' }}>Sound Preview</p>
          <div className="space-y-1">
            {soundTypes.map(type => (
              <div key={type.id} data-testid={`sound-row-${type.id}`}
                className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: '#F0F0F0' }}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: type.id === 'urgent' ? 'rgba(153,27,27,0.08)' : type.id === 'payment' ? 'rgba(22,101,52,0.08)' : type.id === 'overdue' ? 'rgba(89,89,89,0.08)' : 'rgba(212,175,55,0.08)' }}>
                    <Bell className="w-3.5 h-3.5" style={{ color: type.id === 'urgent' ? '#991B1B' : type.id === 'payment' ? '#166534' : type.id === 'overdue' ? '#595959' : '#D4AF37' }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[13px]" style={{ color: '#0F0F0F' }}>{type.label}</p>
                    <p className="text-[11px]" style={{ color: '#595959' }}>{type.description}</p>
                  </div>
                </div>
                <button data-testid={`test-sound-${type.id}`} onClick={() => testSound(type)}
                  disabled={!settings.soundEnabled || settings.nightMode}
                  className="w-9 h-9 flex items-center justify-center border transition-all disabled:opacity-30"
                  style={{ 
                    borderColor: playingId === type.id ? '#D4AF37' : '#E5E5E5',
                    backgroundColor: playingId === type.id ? 'rgba(212,175,55,0.08)' : 'transparent'
                  }}>
                  {playingId === type.id ? (
                    <Check className="w-4 h-4" style={{ color: '#D4AF37' }} strokeWidth={2} />
                  ) : (
                    <Play className="w-3.5 h-3.5" style={{ color: '#595959' }} strokeWidth={1.5} />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Info */}
        <div className="p-3 text-center">
          <p className="text-[10px]" style={{ color: '#BFBFBF' }}>
            Sounds use Web Audio API. No external files needed.
          </p>
          <p className="text-[10px] mt-1" style={{ color: '#BFBFBF' }}>
            Night Mode silences all sounds. Urgent Only plays only for priority orders.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TailorSettings;
